/**
 * Maeiz Workspace design tokens — primitive → semantic → component.
 * Brand locks: royal blue #1E3A8A, authority gold #C9A736.
 */

export const brandPrimitives = {
    blue: {
        DEFAULT: "#1E3A8A",
        light: "#3B82F6",
        dark: "#1E40AF",
    },
    gold: {
        DEFAULT: "#C9A736",
        light: "#F5D061",
        dark: "#B8941F",
    },
} as const;

export const functionalPrimitives = {
    secondary: {
        DEFAULT: "#1FB6FF",
        light: "#4FC5FF",
        dark: "#0D9EE6",
    },
    warning: {
        DEFAULT: "#F59E0B",
        light: "#FBBF24",
        dark: "#D97706",
    },
    success: {
        DEFAULT: "#16A34A",
        light: "#22C55E",
        dark: "#15803D",
    },
    danger: {
        DEFAULT: "#DC2626",
        light: "#EF4444",
        dark: "#B91C1C",
    },
} as const;

export const neutralPrimitives = {
    dark: "#0F172A",
    light: "#F8FAFC",
    border: "#E5E7EB",
    secondary: "#475569",
    /** Near-black brand text / dark-mode ink */
    ink: "#050814",
    /** Dark card / elevated surface */
    navy: "#0B1329",
    /** Dark panel surface */
    surface: "#101A32",
} as const;

export const spaceScale = {
    0: "0px",
    0.5: "2px",
    1: "4px",
    1.5: "6px",
    2: "8px",
    2.5: "10px",
    3: "12px",
    4: "16px",
    5: "20px",
    6: "24px",
    8: "32px",
    10: "40px",
    12: "48px",
    16: "64px",
    20: "80px",
    24: "96px",
} as const;

export const radiusScale = {
    none: "0px",
    sm: "6px",
    md: "10px",
    lg: "12px",
    xl: "16px",
    "2xl": "20px",
    full: "9999px",
} as const;

export const controlSizes = {
    sm: { height: "36px", paddingX: "12px", fontSize: "0.875rem" },
    md: { height: "44px", paddingX: "16px", fontSize: "1rem" },
    lg: { height: "52px", paddingX: "24px", fontSize: "1.125rem" },
} as const;

export const typographyScale = {
    display: { size: "2.25rem", lineHeight: "2.5rem", weight: 800, tracking: "-0.025em", family: "display" },
    pageTitle: { size: "1.875rem", lineHeight: "2.25rem", weight: 800, tracking: "-0.025em", family: "display" },
    section: { size: "1.25rem", lineHeight: "1.75rem", weight: 600, tracking: "-0.01em", family: "display" },
    subsection: { size: "1.125rem", lineHeight: "1.75rem", weight: 600, tracking: "0", family: "display" },
    body: { size: "1rem", lineHeight: "1.5rem", weight: 400, tracking: "0", family: "sans" },
    bodySmall: { size: "0.875rem", lineHeight: "1.25rem", weight: 400, tracking: "0", family: "sans" },
    label: { size: "0.875rem", lineHeight: "1.25rem", weight: 500, tracking: "0", family: "sans" },
    button: { size: "1rem", lineHeight: "1.5rem", weight: 600, tracking: "0", family: "sans" },
    caption: { size: "0.75rem", lineHeight: "1rem", weight: 400, tracking: "0.01em", family: "sans" },
    overline: { size: "0.6875rem", lineHeight: "1rem", weight: 600, tracking: "0.08em", family: "sans" },
    kpi: { size: "1.875rem", lineHeight: "2.25rem", weight: 700, tracking: "-0.02em", family: "mono" },
    mono: { size: "0.875rem", lineHeight: "1.25rem", weight: 400, tracking: "0", family: "mono" },
} as const;

export const fontFamilies = {
    sans: ['"Roboto"', "system-ui", "sans-serif"],
    display: ['"Montserrat"', "system-ui", "sans-serif"],
    mono: ['"JetBrains Mono"', "ui-monospace", "monospace"],
} as const;

export const elevationScale = {
    none: "none",
    sm: "0 1px 2px 0 rgb(15 23 42 / 0.05)",
    md: "0 4px 12px -2px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.04)",
    lg: "0 12px 32px -4px rgb(15 23 42 / 0.12), 0 4px 8px -2px rgb(15 23 42 / 0.06)",
    xl: "0 24px 48px -8px rgb(15 23 42 / 0.18)",
} as const;

export const zIndexScale = {
    base: 0,
    dropdown: 20,
    sticky: 30,
    overlay: 40,
    modal: 50,
    toast: 60,
    tooltip: 70,
} as const;

export const motionDuration = {
    instant: "0ms",
    fast: "120ms",
    normal: "200ms",
    slow: "320ms",
} as const;

export const motionEasing = {
    standard: "cubic-bezier(0.2, 0, 0, 1)",
    emphasized: "cubic-bezier(0.2, 0, 0, 1)",
    exit: "cubic-bezier(0.4, 0, 1, 1)",
} as const;

export const layoutChrome = {
    sidebarExpanded: "260px",
    sidebarCollapsed: "72px",
    topbar: "64px",
    contentMax: "1280px",
    pageGutterMobile: "16px",
    pageGutterDesktop: "24px",
} as const;

export const breakpoints = {
    xs: "480px",
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
} as const;

/** MAEIZ domain status → semantic tone */
export const statusSemantics = {
    pending: "warning",
    open: "info",
    active: "success",
    succeeded: "success",
    paid: "success",
    failed: "danger",
    cancelled: "neutral",
    closed: "neutral",
    synced: "success",
    offline: "warning",
    draft: "neutral",
    verified: "gold",
} as const;

export type StatusKey = keyof typeof statusSemantics;
